import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, Loader2, MoreHorizontal,
  Printer, Mail, Eye, Send, FileText, Palette
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

import { generateDocumentHTML, resolveDocumentTemplate } from '../lib/documentTemplates';
import LineItemsForm from './LineItemsForm';
import AttachmentsSection from './AttachmentsSection';

const DocumentPage = ({
  title,
  description,
  apiEndpoint,
  partyType = 'client', // 'client' or 'supplier'
  partyApi,
  columns = [],
  formFields = [],
    lineItemsEnabled = false,
docType = 'quotation', // quotation, sales_order, purchase_order, bill
}) => {
  const { currentCompany } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  
  const [isConvertOpen, setIsConvertOpen] = useState(false);
  const [convertSO, setConvertSO] = useState(null);
  const [convertLines, setConvertLines] = useState([]);
  const [convertFullyInvoiced, setConvertFullyInvoiced] = useState(false);
  const [converting, setConverting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  
  const [formData, setFormData] = useState({});
  const [emailData, setEmailData] = useState({
    recipient_email: '',
    recipient_name: '',
    subject: '',
    message: ''
  });

  const openConvertDialog = async (doc) => {
    try {
      const res = await api.get(`/sales-orders/${doc.id}/remaining`);
      setConvertSO(doc);
      setConvertLines((res.data.lines || []).map((l) => ({ ...l, convert_quantity: l.remaining_quantity })));
      setConvertFullyInvoiced(!!res.data.fully_invoiced);
      setIsConvertOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load remaining quantities');
    }
  };

  const handleSubmitConvert = async () => {
    const items = convertLines.filter((l) => Number(l.convert_quantity) > 0).map((l) => ({ index: l.index, quantity: Number(l.convert_quantity) }));
    if (items.length === 0) { toast.error('Enter a quantity greater than zero for at least one line'); return; }
    setConverting(true);
    try {
      await api.post(`/sales-orders/${convertSO.id}/convert-to-invoice`, { items });
      toast.success('Invoice created as a draft');
      setIsConvertOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [docsRes, partiesRes, emailStatusRes] = await Promise.all([
        api.get(`/${apiEndpoint}`),
        api.get(`/${partyApi}`),
        api.get('/email/status')
      ]);
      setDocuments(docsRes.data);
      setParties(partiesRes.data);
      setEmailConfigured(emailStatusRes.data.configured);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getPartyName = (partyId) => {
    const party = parties.find(p => p.id === partyId);
    return party?.name || '-';
  };

  const getParty = (partyId) => {
    return parties.find(p => p.id === partyId);
  };

  const partyIdField = partyType === 'client' ? 'client_id' : 'supplier_id';

    const getDocNumber = (doc) => {
    if (doc.document_number) return doc.document_number;
    const prefix = docType === 'sales_order' ? 'SO' : docType === 'quotation' ? 'QUO' : docType === 'purchase_order' ? 'PO' : docType === 'bill' ? 'BILL' : 'DOC';
    return `${prefix}-${doc.id.slice(0, 8).toUpperCase()}`;
  };

const filteredDocs = documents.filter(doc => {
    const party = getParty(doc[partyIdField]);
    return party?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           doc.status?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-slate-100 text-slate-600',
      sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      expired: 'bg-slate-100 text-slate-500',
      pending: 'bg-amber-100 text-amber-700',
      confirmed: 'bg-emerald-100 text-emerald-700',
      processing: 'bg-blue-100 text-blue-700',
      shipped: 'bg-purple-100 text-purple-700',
      delivered: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-600',
      received: 'bg-emerald-100 text-emerald-700',
      paid: 'bg-emerald-100 text-emerald-700',
      overdue: 'bg-red-100 text-red-700'
    };
    return (
      <Badge className={`${colors[status] || colors.draft} font-medium capitalize`}>
        {status?.replace(/_/g, ' ')}
      </Badge>
    );
  };

  const openForm = (doc = null) => {
    if (doc) {
      setEditingDoc(doc);
      setFormData({ ...doc });
      setLineItems(Array.isArray(doc.items) ? doc.items : []);
    } else {
      setEditingDoc(null);
      const initial = {};
      formFields.forEach(f => { initial[f.name] = f.defaultValue ?? ''; });
      setFormData(initial);
      setLineItems([]);
    }
    setIsFormOpen(true);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let payload = formData;
      if (lineItemsEnabled) {
        const items = (lineItems || []).map((item) => {
          const qty = Number(item.quantity) || 0;
          const price = Number(item.unit_price) || 0;
          const rate = Number(item.tax_rate) || 0;
          const sub = Math.round(qty * price * 100) / 100;
          const taxAmt = Math.round(sub * rate) / 100;
          return { ...item, quantity: qty, unit_price: price, tax_rate: rate, subtotal: sub, tax_amount: taxAmt };
        });
        const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
        const tax = items.reduce((sum, item) => sum + item.tax_amount, 0);
        const discount = Number(formData.discount) || 0;
        const discountType = formData.discount_type || "fixed";
        const discountAmount = discountType === "percent" ? Math.round(subtotal * discount) / 100 : discount;
        payload = { ...formData, items, subtotal: Math.round(subtotal * 100) / 100, tax: Math.round(tax * 100) / 100, discount, discount_type: discountType, discount_amount: Math.round(discountAmount * 100) / 100, total: Math.round((subtotal + tax - discountAmount) * 100) / 100 };
      }
      if (editingDoc) {
        await api.put(`/${apiEndpoint}/${editingDoc.id}`, payload);
        toast.success(`${title.slice(0, -1)} updated`);
      } else {
        await api.post(`/${apiEndpoint}`, payload);
        toast.success(`${title.slice(0, -1)} created`);
      }
      setIsFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(`Delete this ${title.slice(0, -1).toLowerCase()}?`)) {
      try {
        await api.delete(`/${apiEndpoint}/${id}`);
        toast.success('Deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  // Get party details for a document
  const getPartyDetails = (partyId) => {
    const party = parties.find(p => p.id === partyId);
    return party || {};
  };

  const openPreview = (doc) => {
    setSelectedDoc(doc);
    const party = getPartyDetails(doc.client_id || doc.supplier_id);
    const docWithParty = {
      ...doc,
      client_name: party?.name || 'Client',
      client_email: party?.email || '',
      client_phone: party?.phone || '',
      client_address: party?.address || '',
      supplier_name: party?.name || 'Supplier',
      supplier_email: party?.email || '',
      supplier_phone: party?.phone || '',
      supplier_address: party?.address || '',
    };
    
    const html = generateDocumentHTML(resolveDocumentTemplate(docWithParty, currentCompany, docType), docType, docWithParty, currentCompany);
    setPreviewHtml(html);
    setIsPreviewOpen(true);
  };

  
    

  const handlePrint = (doc) => {
    const party = getPartyDetails(doc.client_id || doc.supplier_id);
    const docWithParty = {
      ...doc,
      client_name: party?.name || 'Client',
      client_email: party?.email || '',
      client_phone: party?.phone || '',
      client_address: party?.address || '',
      supplier_name: party?.name || 'Supplier',
      supplier_email: party?.email || '',
      supplier_phone: party?.phone || '',
      supplier_address: party?.address || '',
    };
    
    const html = generateDocumentHTML(resolveDocumentTemplate(docWithParty, currentCompany, docType), docType, docWithParty, currentCompany);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const openEmailDialog = (doc) => {
    const party = getParty(doc[partyIdField]);
    setSelectedDoc(doc);
    setEmailData({
      recipient_email: party?.email || '',
      recipient_name: party?.name || '',
      subject: `${title.slice(0, -1)} #${getDocNumber(doc)} from Elite Group`,
      message: `Please find attached your ${title.slice(0, -1).toLowerCase()}. Let us know if you have any questions.`
    });
    setIsEmailOpen(true);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailConfigured) {
      toast.error('Email service not configured');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/${apiEndpoint}/${selectedDoc.id}/send-email`, emailData);
      toast.success(`Sent to ${emailData.recipient_email}`);
      setIsEmailOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSubmitting(false);
    }
  };

  const renderFormField = (field) => {
    const value = formData[field.name] ?? '';
    
    if (field.type === 'select') {
      return (
        <Select value={String(value)} onValueChange={(val) => setFormData({...formData, [field.name]: val})}>
          <SelectTrigger><SelectValue placeholder={`Select ${field.label}`} /></SelectTrigger>
          <SelectContent>
            {field.options?.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    
    if (field.type === 'party') {
      return (
        <Select value={String(value)} onValueChange={(val) => setFormData({...formData, [field.name]: val})}>
          <SelectTrigger><SelectValue placeholder={`Select ${partyType}`} /></SelectTrigger>
          <SelectContent>
            {parties.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    
    return (
      <Input
        type={field.type || 'text'}
        value={value}
        onChange={(e) => {
          const val = field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
          setFormData({...formData, [field.name]: val});
        }}
        placeholder={field.placeholder}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-slate-500 mt-1">{description}</p>
        </div>
        <Button onClick={() => openForm()} className="bg-teal-700 hover:bg-teal-800" data-testid={`add-${docType}-btn`}>
          <Plus className="h-4 w-4 mr-2" />
          Create New
        </Button>
      </div>

      {!emailConfigured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <Mail className="h-5 w-5 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-800">Email not configured</p>
            <p className="text-xs text-amber-600">Add RESEND_API_KEY to enable sending via email</p>
          </div>
        </div>
      )}

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={`Search ${title.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-10 border-slate-300"
        />
      </div>

      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Doc #</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">{partyType === 'client' ? 'Client' : 'Supplier'}</TableHead>
                  {columns.map(col => (
                    <TableHead key={col.key} className="text-xs font-semibold text-slate-500 uppercase">{col.label}</TableHead>
                  ))}
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocs.length > 0 ? (
                  filteredDocs.map((doc) => (
                    <TableRow key={doc.id} className="hover:bg-slate-50/50">
                      <TableCell className="font-mono text-sm text-slate-600">{getDocNumber(doc)}</TableCell>
                      <TableCell className="text-sm font-medium text-slate-900">{getPartyName(doc[partyIdField])}</TableCell>
                      {columns.map(col => (
                        <TableCell key={col.key} className="text-sm text-slate-700">
                          {col.type === 'currency' ? formatCurrency(doc[col.key]) : 
                           col.type === 'date' ? (doc[col.key] || '-') : 
                           (doc[col.key] ?? '-')}
                        </TableCell>
                      ))}
                      <TableCell>{getStatusBadge(doc.status)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openPreview(doc)}>
                              <Eye className="h-4 w-4 mr-2" />Preview
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handlePrint(doc)}>
                              <Printer className="h-4 w-4 mr-2" />Print
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEmailDialog(doc)} disabled={!emailConfigured}>
                              <Mail className="h-4 w-4 mr-2" />Send Email
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openForm(doc)}>
                              <Edit className="h-4 w-4 mr-2" />Edit
                            </DropdownMenuItem>
          {docType === 'sales_order' && (
            <DropdownMenuItem onClick={() => openConvertDialog(doc)}>
              <FileText className="h-4 w-4 mr-2" />Convert to Invoice
            </DropdownMenuItem>
          )}
                            <DropdownMenuItem onClick={() => handleDelete(doc.id)} className="text-red-600">
                              <Trash2 className="h-4 w-4 mr-2" />Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length + 4} className="h-32 text-center text-slate-500">
                      <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      No {title.toLowerCase()} found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className={lineItemsEnabled ? "sm:max-w-4xl max-h-[90vh] overflow-y-auto" : "sm:max-w-lg"}>
          <DialogHeader>
            <DialogTitle className="font-heading">{editingDoc ? 'Edit' : 'Create'} {title.slice(0, -1)}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitForm}>
            <div className="grid gap-4 py-4">
              {formFields.filter(field => (lineItemsEnabled === false || ["subtotal", "tax", "total", "client_id", "supplier_id"].indexOf(field.name) === -1)).map(field => (
                <div key={field.name} className="grid gap-2">
                  <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
                  {renderFormField(field)}
                </div>
              ))}
              {lineItemsEnabled && (
                <LineItemsForm
                  items={lineItems}
                  onItemsChange={setLineItems}
                  clients={parties}
                  onClientsChange={setParties}
                  selectedClientId={formData.client_id || formData.supplier_id}
                  onClientChange={(val) => setFormData({ ...formData, ...(partyType === "supplier" ? { supplier_id: val } : { client_id: val }) })}
                  discount={formData.discount || 0}
                  onDiscountChange={(val) => setFormData({ ...formData, discount: val })}
                  discountType={formData.discount_type || "fixed"}
                  onDiscountTypeChange={(val) => setFormData({ ...formData, discount_type: val })}
                  partyType={partyType}
                  partyLabel={partyType === "supplier" ? "Supplier" : "Client"}
                />
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingDoc ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5 text-teal-600" />{title.slice(0, -1)} Preview
            </DialogTitle>
            <DialogDescription>
              This document renders with the company's default template from Settings.
            </DialogDescription>
          </DialogHeader>
          
          
             
              
          <div className="border rounded-lg overflow-hidden mt-4" dangerouslySetInnerHTML={{ __html: previewHtml}} />
      {(docType === 'sales_order' || docType === 'quotation') && (<AttachmentsSection entityType={docType} entityId={selectedDoc?.id} />)}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Close</Button>
            <Button variant="outline" onClick={() => selectedDoc && handlePrint(selectedDoc)}>
              <Printer className="h-4 w-4 mr-2" />Print
            </Button>
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={() => { setIsPreviewOpen(false); selectedDoc && openEmailDialog(selectedDoc); }} disabled={!emailConfigured}>
              <Mail className="h-4 w-4 mr-2" />Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={isEmailOpen} onOpenChange={setIsEmailOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Send className="h-5 w-5 text-teal-600" />Send via Email
            </DialogTitle>
            <DialogDescription>Send {title.slice(0, -1).toLowerCase()} #{selectedDoc?.id.slice(0, 8).toUpperCase()}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendEmail}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Recipient Email *</Label>
                <Input type="email" value={emailData.recipient_email} onChange={(e) => setEmailData({...emailData, recipient_email: e.target.value})} required />
              </div>
              <div className="grid gap-2">
                <Label>Recipient Name *</Label>
                <Input value={emailData.recipient_name} onChange={(e) => setEmailData({...emailData, recipient_name: e.target.value})} required />
              </div>
              <div className="grid gap-2">
                <Label>Subject</Label>
                <Input value={emailData.subject} onChange={(e) => setEmailData({...emailData, subject: e.target.value})} />
              </div>
              <div className="grid gap-2">
                <Label>Message</Label>
                <Textarea value={emailData.message} onChange={(e) => setEmailData({...emailData, message: e.target.value})} rows={3} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEmailOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting}>
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</> : <><Send className="h-4 w-4 mr-2" />Send</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    {docType === 'sales_order' && (
      <Dialog open={isConvertOpen} onOpenChange={setIsConvertOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Convert to Invoice</DialogTitle>
            <DialogDescription>
              {convertFullyInvoiced
                ? 'This sales order is fully invoiced.'
                : `Convert quantities from sales order ${convertSO ? getDocNumber(convertSO) : ''} into a new draft invoice.`}
            </DialogDescription>
          </DialogHeader>
          {!convertFullyInvoiced && (
            <div className="grid gap-3 py-2">
              {convertLines.map((line) => (
                <div key={line.index} className="grid grid-cols-3 items-center gap-3">
                  <div className="col-span-2">
                    <div className="text-sm font-medium">{line.product_name}</div>
                    <div className="text-xs text-[#5C6660]">Remaining {line.remaining_quantity} of {line.quantity} (already invoiced {line.invoiced_quantity})</div>
                  </div>
                  <Input type="number" min="0" max={line.remaining_quantity} step="1" value={line.convert_quantity} onChange={(e) => setConvertLines((prev) => prev.map((l) => (l.index === line.index ? { ...l, convert_quantity: e.target.value } : l)))} data-testid={`convert-qty-${line.index}`} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsConvertOpen(false)}>Cancel</Button>
            {!convertFullyInvoiced && (
              <Button type="button" className="bg-teal-700 hover:bg-teal-800" disabled={converting} onClick={handleSubmitConvert} data-testid="convert-submit-btn">
                {converting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Converting...</>) : 'Create Invoice'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </div>
  );
};

export default DocumentPage;
